import React, { useEffect, useCallback } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { keymap } from '@codemirror/view';
import { useAppContext } from '../../context/AppContext';
import { formatDSLExpression, getAutoIndent } from '../../utils/smartIndentation';
import { preprocessDSLInput, validateBracketSyntax } from '../../utils/dslPreprocessor';
import { dslAutocompletion } from './dslAutocompletion';

const CodeEditor = () => {
  const { dslInput, setDslInput, showToast } = useAppContext();

  // Handle Enter key for auto-indentation
  const handleEnterKey = useCallback((view) => {
    const { state } = view;
    const { selection } = state;
    const line = state.doc.lineAt(selection.main.head);
    const lineText = line.text;
    const cursorPos = selection.main.head - line.from;
    
    // Get the full document text to properly detect brace context
    const fullText = state.doc.toString();
    const beforeCursor = fullText.substring(0, selection.main.head);
    
    // Check if we're inside curly braces by counting braces in the entire document up to cursor
    const openBraces = (beforeCursor.match(/\{/g) || []).length;
    const closeBraces = (beforeCursor.match(/\}/g) || []).length;
    const insideBraces = openBraces > closeBraces;
    
    if (insideBraces) {
      // Use the current line and cursor position for indentation calculation
      const autoIndent = getAutoIndent(lineText, cursorPos, true);
      
      // Insert newline with auto-indentation
      view.dispatch({
        changes: {
          from: selection.main.head,
          insert: '\n' + autoIndent
        },
        selection: {
          anchor: selection.main.head + 1 + autoIndent.length
        }
      });
      
      return true; // Prevent default behavior
    }
    
    return false; // Allow default behavior
  }, []);

  // Handle Ctrl+Shift+F for formatting
  const handleFormat = useCallback((view) => {
    const { state } = view;
    const currentText = state.doc.toString();
    const formatted = formatDSLExpression(currentText);
    
    if (formatted !== currentText) {
      view.dispatch({
        changes: {
          from: 0,
          to: state.doc.length,
          insert: formatted
        }
      });
      showToast('Expression formatted', 'info');
    }
    
    return true;
  }, [showToast]);

  // Custom keymap for smart features
  const customKeymap = keymap.of([
    {
      key: 'Enter',
      run: handleEnterKey
    },
    {
      key: 'Ctrl-Shift-f',
      run: handleFormat
    },
    {
      key: 'Cmd-Shift-f', // Mac equivalent
      run: handleFormat
    }
  ]);

  // Handle input changes with validation
  const handleInputChange = useCallback((value) => {
    setDslInput(value);
    
    // Validate bracket syntax
    const validation = validateBracketSyntax(value);
    if (!validation.isValid) {
      console.warn('Bracket syntax error:', validation.error);
    }
  }, [setDslInput]);

  return (
    <div className="code-editor-container">
      <CodeMirror
        value={dslInput}
        height="100%"
        theme={vscodeDark}
        extensions={[javascript(), customKeymap, dslAutocompletion]}
        onChange={handleInputChange}
        placeholder="Enter your ./math expressions here...\nUse { } for multi-line complex expressions\nPress Ctrl+Shift+F to format"
        basicSetup={{
          lineNumbers: true,
          highlightActiveLine: true,
          highlightSelectionMatches: true,
          autocompletion: false, // Disable default, use our custom one
          bracketMatching: true,
          closeBrackets: false,
          searchKeymap: true,
          allowMultipleSelections: false,
          scrollPastEnd: false,
        }}
        style={{
          height: '100%',
          overflow: 'auto'
        }}
      />
    </div>
  );
};

export default CodeEditor;